/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { get } from 'utils/apiClient'
import { IApiResponse } from 'utils/apiInterfaces'
import apiEndPoints from 'utils/constants/apiEndpoints'

export interface ICountry {
  countryName: string
  countryCode: string
  code: number
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IProduct {
  productId: number
  productName: string
  productCode: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IPlatform {
  platformId: number
  platformName: string
  platformCode: string
  appCode: null
  keyName: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IPaymentProvider {
  providerName: string
  providerImage: string
  paymentMode: number
  additional: null
  verifyPaymentStatus: number
  json: string
  communicationTrigger: null
  providerId: number
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IPaymentMode {
  paymentId: number
  paymentName: number
  paymentImage: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface ICommonData {
  countries: { label: string; value: string }[]
  countriesList: ICountry[]
  products: { label: string; value: string }[]
  platforms: { label: string; value: string }[]
  paymentProviders: { label: string; value: string }[]
  paymentModes: { label: string; value: string }[]
}

export const fetchCountryList = createAsyncThunk(
  'auth/fetchCountryList',
  async () => {
    const countriesData: IApiResponse<ICountry[]> = await get(
      `${apiEndPoints.CRM_COUNTRY_LIST}?page=1&limit=250`
    )
    return countriesData.data
  }
)

export const fetchProductList = createAsyncThunk(
  'auth/fetchProductsList',
  async () => {
    const productData: IApiResponse<IProduct[]> = await get(
      `${apiEndPoints.CRM_PRODUCT_LIST}?page=1&limit=1000`
    )
    return productData.data
  }
)

export const fetchPlatformList = createAsyncThunk(
  'auth/fetchPlatformList',
  async () => {
    const platformData: IApiResponse<IPlatform[]> = await get(
      `${apiEndPoints.CRM_PLATFORM_LIST}?page=1&limit=1000`
    )
    return platformData.data
  }
)

export const fetchPaymentProviderList = createAsyncThunk(
  'auth/fetchPaymentProviderList',
  async () => {
    const providerData: IApiResponse<IPaymentProvider[]> = await get(
      `${apiEndPoints.CRM_PROVIDER_LIST}?page=1&limit=1000`
    )
    return providerData.data
  }
)

export const fetchPaymentModeList = createAsyncThunk(
  'auth/fetchPaymentModeList',
  async () => {
    const modeData: IApiResponse<IPaymentMode[]> = await get(
      `${apiEndPoints.CRM_MODE_LIST}?page=1&limit=10000`
    )
    return modeData.data
  }
)

const initialState: ICommonData = {
  countries: [],
  countriesList: [],
  products: [],
  platforms: [],
  paymentProviders: [],
  paymentModes: [],
}

const crmCommonDataSlice = createSlice({
  name: 'userInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountryList.pending, (state) => {
        state.countries = []
        state.countriesList = []
      })
      .addCase(
        fetchCountryList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.countries = action.payload.map((country: ICountry) => ({
            label: country.countryName,
            value: country.countryCode,
          }))

          state.countriesList = action.payload
        }
      )
      .addCase(fetchCountryList.rejected, (state) => {
        state.countries = []
        state.countriesList = []
      })
      .addCase(fetchProductList.pending, (state) => {
        state.products = []
      })
      .addCase(
        fetchProductList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.products = action.payload.map((product: IProduct) => ({
            label: product.productName,
            value: product.productCode,
          }))
        }
      )
      .addCase(fetchProductList.rejected, (state) => {
        state.products = []
      })
      .addCase(fetchPlatformList.pending, (state) => {
        state.platforms = []
      })
      .addCase(
        fetchPlatformList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.platforms = action.payload.map((platform: IPlatform) => ({
            label: platform.platformName,
            value: platform.platformCode,
          }))
        }
      )
      .addCase(fetchPlatformList.rejected, (state) => {
        state.platforms = []
      })
      .addCase(fetchPaymentProviderList.pending, (state) => {
        state.paymentProviders = []
      })
      .addCase(
        fetchPaymentProviderList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.paymentProviders = action.payload.map(
            (provider: IPaymentProvider) => ({
              label: provider.providerName,
              value: provider.providerId,
            })
          )
        }
      )
      .addCase(fetchPaymentProviderList.rejected, (state) => {
        state.paymentProviders = []
      })
      .addCase(fetchPaymentModeList.pending, (state) => {
        state.paymentModes = []
      })
      .addCase(
        fetchPaymentModeList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.paymentModes = action.payload.map((mode: IPaymentMode) => ({
            label: mode.paymentName,
            value: mode.paymentId,
          }))
        }
      )
      .addCase(fetchPaymentModeList.rejected, (state) => {
        state.paymentModes = []
      })
  },
})

export default crmCommonDataSlice.reducer
